import { AnimatePresence, motion, Reorder } from "motion/react"
import {
  ComponentType,
  type CSSProperties,
  useCallback,
  useRef,
  useState,
} from "react"

const COLLAPSE_DURATION = 600

interface CollapsibleListProps<T extends { id: string }> {
  items: T[]
  summary: ComponentType<{ item: T; isExpanded: boolean }>
  detail: ComponentType<{ item: T; onCollapse: () => void }>
  canExpand?: (item: T) => boolean
  gap?: number
  separator?: boolean
  noScroll?: boolean
  style?: CSSProperties
}

const CollapsibleList = <T extends { id: string }>({
  items,
  summary: Summary,
  detail: Detail,
  canExpand,
  gap = 0,
  separator = false,
  noScroll = false,
  style,
}: CollapsibleListProps<T>) => {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [collapsingId, setCollapsingId] = useState<string | null>(null)
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const collapse = useCallback(() => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current)
    setCollapsingId(expandedId)
    collapseTimer.current = setTimeout(() => {
      setExpandedId(null)
      setCollapsingId(null)
    }, COLLAPSE_DURATION)
  }, [expandedId])

  const handleClick = useCallback(
    (id: string) => {
      if (expandedId === id) {
        collapse()
      } else {
        if (collapseTimer.current) clearTimeout(collapseTimer.current)
        setCollapsingId(null)
        setExpandedId(id)
      }
    },
    [expandedId, collapse]
  )

  const containerStyle: CSSProperties = {
    overflowY: noScroll ? "visible" : "auto",
    height: "100%",
    width: "100%",
    paddingBottom: noScroll ? 0 : 100,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: separator ? 0 : gap,
    ...style,
  }

  return (
    <Reorder.Group
      axis="y"
      onReorder={() => {}}
      layoutScroll
      values={items}
      style={containerStyle}
    >
      <AnimatePresence mode="popLayout">
        {items.map((item, index) => {
          const isExpanded = expandedId === item.id && collapsingId !== item.id
          const showDetail = isExpanded && !!canExpand?.(item)

          const itemStyle: CSSProperties = {}
          if (separator && index < items.length - 1) {
            itemStyle.borderBottom = "1px solid #d9d9d9"
            itemStyle.paddingBottom = "12px"
            itemStyle.marginBottom = "12px"
          }

          return (
            <Reorder.Item
              key={item.id}
              value={item}
              layout="position"
              initial={{ opacity: 0, x: 4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -4 }}
              transition={{
                duration: 0.5,
                ease: "easeInOut",
                layout: { duration: 0.4, ease: "easeInOut" },
              }}
              style={{ ...itemStyle, width: "100%", cursor: "pointer" }}
              onClick={() => handleClick(item.id)}
            >
              <Summary item={item} isExpanded={isExpanded} />
              <AnimatePresence initial={false}>
                {showDetail && (
                  <motion.div
                    key="detail"
                    initial={{ height: 0, opacity: 0, overflow: "hidden" }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                      overflow: "hidden",
                      transition: {
                        height: { duration: 0.3 },
                        opacity: { duration: 0.2, delay: 0.1 },
                      },
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                      overflow: "hidden",
                      transition: {
                        opacity: { duration: 0.15 },
                        height: { duration: 0.3, delay: 0.1 },
                      },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Detail item={item} onCollapse={collapse} />
                  </motion.div>
                )}
              </AnimatePresence>
            </Reorder.Item>
          )
        })}
      </AnimatePresence>
    </Reorder.Group>
  )
}

export default CollapsibleList
